import { isEqual, sortBy } from 'lodash-es';
import { type Reducer, useEffect, useReducer, useRef } from 'react';
import useFriends from '@/hooks/contacts/use-friends';
import useConversationSessions from '@/hooks/sessions/use-conversation-sessions';
import type { ConversationSessionsTypeUnion } from '@/models/conversations/conversation-session';
import type { Friend } from '@/models/user/friend';

export default function useSortedFriends() {
  const { friends, isLoading } = useFriends();

  const prevFriendsRef = useRef<Friend[]>();

  const { availableSessions: recentSessions } = useConversationSessions();

  const [state, dispatch] = useReducer<SortFriendReducer>(
    (state, action) => sortFriendReducer(state, action, { sessions: recentSessions }),
    {
      sortedBy: 'by-befriend-time',
      friends: sortFriendByFilterType('by-befriend-time', Object.values(friends), recentSessions),
    },
  );

  useEffect(() => {
    const currentFriends = Object.values(friends);

    if (isEqual(currentFriends, prevFriendsRef.current)) return;

    prevFriendsRef.current = currentFriends;

    dispatch({
      action: 'update-friends',
      friends: currentFriends,
    });
  }, [friends]);

  return {
    isLoading,
    friends,
    sortedFriends: state.friends,
    sortBy: state.sortedBy,
    setSortBy: (filter: SortFriendReducerFilterType) => dispatch({ action: 'change-sort', filter }),
  } as const;
}

export const sortOptions: { value: SortFriendReducerFilterType; label: string }[] = [
  { value: 'by-befriend-time', label: 'contact.sort.recent-join' },
  { value: 'by-name', label: 'contact.sort.name' },
  { value: 'by-message-time', label: 'contact.sort.recent-chat' },
];

export type SortFriendReducerFilterType = 'by-name' | 'by-befriend-time' | 'by-message-time';

type SortFriendReducerAction =
  | { action: 'change-sort'; filter: SortFriendReducerFilterType }
  | { action: 'update-friends'; friends: Friend[] };

type SortFriendReducerState = {
  sortedBy: SortFriendReducerFilterType;
  friends: Friend[];
};

type SortFriendReducer = Reducer<SortFriendReducerState, SortFriendReducerAction>;

function sortFriendReducer(
  state: SortFriendReducerState,
  action: SortFriendReducerAction,
  refs: { sessions: ConversationSessionsTypeUnion[] },
): SortFriendReducerState {
  switch (action.action) {
    case 'change-sort':
      return {
        sortedBy: action.filter,
        friends: sortFriendByFilterType(action.filter, state.friends, refs.sessions),
      };

    case 'update-friends':
      return {
        sortedBy: state.sortedBy,
        friends: sortFriendByFilterType(state.sortedBy, action.friends, refs.sessions),
      };

    default:
      return state;
  }
}

export function sortFriendByFilterType(
  filter: SortFriendReducerFilterType,
  friends: Friend[],
  sessions: ConversationSessionsTypeUnion[],
): Friend[] {
  switch (filter) {
    case 'by-name':
      return sortBy(friends, (item) => item.Order);
    case 'by-message-time': {
      const [friendsWithTimestamps, friendsWithoutTimestamps] = friends.reduce<
        [Array<Friend & { lastMessageTimestamp: number }>, Friend[]]
      >(
        (acc, friend) => {
          const session = sessions.find((s) => s.SessionType === 'session' && s.UserID === friend.FriendID);
          if (session?.LastMsgTimeStamp) {
            acc[0].push({ ...friend, lastMessageTimestamp: session.LastMsgTimeStamp });
          } else {
            acc[1].push(friend);
          }
          return acc;
        },
        [[], []],
      );

      return [
        ...sortBy(friendsWithTimestamps, (friend) => -friend.lastMessageTimestamp),
        ...sortBy(friendsWithoutTimestamps, (friend) => friend.Order),
      ];
    }
    case 'by-befriend-time':
      return sortBy(friends, (item) => -item.CreateTime);
    default:
      return friends;
  }
}
